// client/src/index.js

import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { CacheProvider } from '@emotion/react';
import './index.css';
// Fontsource — self-hosted, no CDN dependency
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/700.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/600.css';
import '@fontsource/dm-sans/400.css';
import '@fontsource/dm-sans/500.css';
import '@fontsource/lora/400.css';
import '@fontsource/lora/600.css';
import '@fontsource/playfair-display/400.css';
import '@fontsource/playfair-display/700.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/fira-code/400.css';
import '@fontsource/nunito/400.css';
import '@fontsource/nunito/700.css';
import '@fontsource/quicksand/400.css';
import '@fontsource/quicksand/600.css';
import App from './App';
import createEmotionCache from './createEmotionCache';
import { UserPreferencesProvider } from './contexts/UserPreferencesContext';
import axios from 'axios';

// Create emotion cache
const cache = createEmotionCache();

// Configure axios defaults
const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
axios.defaults.baseURL = apiBaseUrl;
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.withCredentials = true; // Important for CORS

// Add token to requests if it exists
const token = localStorage.getItem('token');
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// Add axios response interceptor for token handling
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Error suppression after imports
const originalError = console.error;
console.error = (...args) => {
  if (args[0]?.includes?.('bootstrap-legacy-autofill-overlay')) {
    return;
  }
  originalError.apply(console, args);
};

// Add after imports
if (typeof window !== 'undefined') {
  // Prevent the script from executing
  window.preventBootstrapAutofill = true;
  
  // Override any attempts to modify the service
  Object.defineProperty(window, 'CollectAutofillOverlayService', {
    value: {
      domQueryService: {
        checkPageContainsShadowDom: () => false,
        getDocumentRoot: () => document,
        getDocumentElement: () => document.documentElement,
        querySelectorAll: () => []
      },
      processMutations: () => {},
      handleMutationObserverMutation: () => {},
      init: () => {}
    },
    writable: false,
    configurable: false
  });
}

const root = createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <CacheProvider value={cache}>
      <BrowserRouter>
        <UserPreferencesProvider>
          <App />
        </UserPreferencesProvider>
      </BrowserRouter>
    </CacheProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
//reportWebVitals();
